/**
 * DatasetPickerModal - Select an active dataset to upload a new snapshot to.
 * @module components/starnexus/datasets/DatasetPickerModal
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '@amzn/awsui-components-console/modal';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Select, { SelectProps } from '@amzn/awsui-components-console/select';
import Alert from '@amzn/awsui-components-console/alert';
import type { Dataset } from '../../../types/starnexus';

export interface DatasetPickerModalProps {
  visible: boolean;
  onDismiss: () => void;
  datasets: Dataset[];
}

export const DatasetPickerModal: React.FC<DatasetPickerModalProps> = ({ visible, onDismiss, datasets }) => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<SelectProps.Option | null>(null);

  const activeDatasets = datasets.filter((d) => d.status === 'active');

  const options: SelectProps.Options = activeDatasets.map((d) => ({
    label: d.datasetName,
    value: d.datasetId,
    description: d.providerName && d.tableName ? `${d.providerName}.${d.tableName}` : undefined,
  }));

  const handleContinue = () => {
    if (!selected?.value) return;
    onDismiss();
    navigate(`/starnexus/uploader/datasets/${selected.value}?action=upload`);
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="Upload new snapshot"
      size="medium"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>Cancel</Button>
            <Button variant="primary" disabled={!selected} onClick={handleContinue}>Continue</Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box>Select an active dataset to upload a new data snapshot to.</Box>
        {activeDatasets.length === 0 ? (
          <Alert type="info">No active datasets available. Create a dataset first.</Alert>
        ) : (
          <Select
            selectedOption={selected}
            onChange={({ detail }) => setSelected(detail.selectedOption)}
            options={options}
            placeholder="Choose a dataset"
            filteringType="auto"
          />
        )}
      </SpaceBetween>
    </Modal>
  );
};

export default DatasetPickerModal;
